import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TableCell,
  TextField,
} from "@material-ui/core";
import { DeleteOutline } from "@material-ui/icons";
import { CTable, ErrorBox, HFlex, Loading, SearchPagination, Text, VFlex } from "shared";
import CCell from "shared/CTable/CCell";
import { CDialog } from "shared";
import useListInvitations from "./queries/useListInvitations";
import useDeleteInvitation from "./queries/useDeleteInvitation";
import useResendInvitation from "./queries/useResendInvitation";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Area } from "types";
import { join } from "utils/misc";
import { InvitationRecord, InvitationSource, InvitationsParams } from "api/invitationTypes";
import { useErrorContext } from "shared/ErrorModal";
import { getDate } from "utils/time";
import { Colors } from "theme";

type InvitationRow = InvitationRecord & { source: string; status: string; reminders: number; actions: "" };

const Invitations: React.VFC = () => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const history = useHistory();
  const { search: locationSearch } = useLocation();
  const therapistIdParam = new URLSearchParams(locationSearch).get("therapist_id");
  const therapistIdParsed = therapistIdParam ? parseInt(therapistIdParam, 10) : NaN;
  const therapistId = isNaN(therapistIdParsed) ? undefined : therapistIdParsed;

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState<InvitationsParams["status"]>("unclaimed");
  const [source, setSource] = useState<InvitationSource | "all">("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(25);

  const [deleteTarget, setDeleteTarget] = useState<InvitationRecord | null>(null);
  const [resendSuccess, setResendSuccess] = useState<number | null>(null);
  const resendTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resendTimeoutRef.current) clearTimeout(resendTimeoutRef.current);
    },
    []
  );

  const params: InvitationsParams = {
    status,
    source: source === "all" ? undefined : source,
    search: search || undefined,
    therapist_id: therapistId,
    page: currentPage + 1, // MUI TablePagination is 0-indexed, Go backend is 1-indexed
    limit: pageLimit,
  };

  const { data, error, isLoading, isLoadingError: isError, isSuccess } = useListInvitations(params);
  const { mutateAsync: deleteInvitation, isLoading: isDeleting } = useDeleteInvitation();
  const { mutateAsync: resendInvitation, isLoading: isResending } = useResendInvitation();

  useEffect(() => {
    setCurrentPage(0);
  }, [search, status, source, therapistId]);

  const isFiltered = searchInput || search || status !== "unclaimed" || source !== "all" || !!therapistId;

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setStatus("unclaimed");
    setSource("all");
    if (therapistId) history.replace(Area.Invitations);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteInvitation(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      setError(t("invitations.delete_error"), err);
    }
  };

  const handleResend = async (id: number) => {
    if (resendTimeoutRef.current) clearTimeout(resendTimeoutRef.current);
    setResendSuccess(null);
    try {
      await resendInvitation(id);
      setResendSuccess(id);
      resendTimeoutRef.current = setTimeout(() => setResendSuccess(null), 3000);
    } catch (err) {
      setError(t("invitations.resend_error"), err);
    }
  };

  const tableHeader = {
    id: t("invitations.column_id"),
    name: t("invitations.column_name"),
    email: t("invitations.column_email"),
    phone_number: t("invitations.column_phone"),
    source: t("invitations.column_source"),
    sent_at: t("invitations.column_sent_at"),
    last_reminder_at: t("invitations.column_last_reminder"),
    reminders: t("invitations.column_reminders"),
    status: t("invitations.column_status"),
    actions: "",
  };

  const tableCellFactory = (item: InvitationRow) => ({
    id: (
      <TableCell>
        <Text tiny>{item.id}</Text>
      </TableCell>
    ),
    name: (
      <CCell>
        {item.patient_id ? (
          <Link to={join(Area.Patients, item.patient_id)} style={{ color: Colors.JA_BLUE }}>
            {item.name ?? item.patient_id}
          </Link>
        ) : (
          item.name ?? "—"
        )}
      </CCell>
    ),
    email: <CCell>{item.email ?? "—"}</CCell>,
    phone_number: <CCell>{item.phone_number ?? "—"}</CCell>,
    source: (
      <CCell>
        {item.therapist_id ? (
          <Link to={join(Area.Therapists, item.therapist_id)} style={{ color: Colors.JA_BLUE }}>
            {t("invitations.source_therapist")}: {item.therapist_name ?? item.therapist_id}
          </Link>
        ) : item.affiliate_name ? (
          `${t("invitations.source_affiliate")}: ${item.affiliate_name}`
        ) : (
          "—"
        )}
      </CCell>
    ),
    sent_at: <CCell>{item.sent_at ? getDate(item.sent_at) : "—"}</CCell>,
    last_reminder_at: <CCell>{item.last_reminder_at ? getDate(item.last_reminder_at) : "—"}</CCell>,
    reminders: <CCell>{item.reminder_count}</CCell>,
    status: <CCell>{item.claimed ? t("invitations.status_claimed") : t("invitations.status_unclaimed")}</CCell>,
    actions: (
      <TableCell>
        <HFlex gap={1}>
          {!item.claimed && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={isResending}
              onClick={() => handleResend(item.id)}
            >
              {resendSuccess === item.id ? t("invitations.resend_success") : t("buttons.resend")}
            </Button>
          )}
          <IconButton size="small" onClick={() => setDeleteTarget(item)} style={{ color: Colors.ERROR }}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </HFlex>
      </TableCell>
    ),
  });

  const pagination = data?.metadata?.pagination;

  return (
    <VFlex>
      <Box mt={12} mb={3}>
        <Container maxWidth="lg">
          <Text xlarge bold mb={3}>
            {t("invitations.title")}
          </Text>
          <form onSubmit={handleSearchSubmit}>
            <HFlex gap={2} alignItems="center" mb={3} flexWrap="wrap">
              <TextField
                label={t("invitations.filter_search")}
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                variant="outlined"
                size="medium"
                style={{ width: 320 }}
              />
              <FormControl variant="outlined" size="medium" style={{ minWidth: 140 }}>
                <InputLabel>{t("invitations.filter_status")}</InputLabel>
                <Select
                  value={status}
                  onChange={e => setStatus(e.target.value as InvitationsParams["status"])}
                  label={t("invitations.filter_status")}
                >
                  <MenuItem value="unclaimed">{t("invitations.status_unclaimed")}</MenuItem>
                  <MenuItem value="claimed">{t("invitations.status_claimed")}</MenuItem>
                  <MenuItem value="all">{t("invitations.status_all")}</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="medium" style={{ minWidth: 140 }}>
                <InputLabel>{t("invitations.filter_source")}</InputLabel>
                <Select
                  value={source}
                  onChange={e => setSource(e.target.value as InvitationSource | "all")}
                  label={t("invitations.filter_source")}
                >
                  <MenuItem value="all">{t("invitations.source_all")}</MenuItem>
                  <MenuItem value="therapist">{t("invitations.source_therapist")}</MenuItem>
                  <MenuItem value="affiliate">{t("invitations.source_affiliate")}</MenuItem>
                </Select>
              </FormControl>
              <Button type="submit" variant="contained" color="primary" size="medium">
                {t("buttons.search")}
              </Button>
              {isFiltered && (
                <Button type="button" variant="outlined" size="medium" onClick={handleReset}>
                  {t("buttons.reset")}
                </Button>
              )}
            </HFlex>
          </form>
        </Container>
      </Box>

      <Box bgcolor="background.paper" py={2} overflow="auto">
        {isError && <ErrorBox error={error!} verbose centered dark />}
        {isLoading && <Loading />}
        {isSuccess && (
          <>
            {(data?.items?.length ?? 0) === 0 ? (
              <Text bold secondary paragraph mb={0} center>
                {t("invitations.no_invitations")}
              </Text>
            ) : (
              <Container maxWidth={false}>
                <CTable
                  header={tableHeader}
                  items={data?.items?.map((item): InvitationRow => ({
                    ...item,
                    source: item.therapist_id ? String(item.therapist_name ?? item.therapist_id) : (item.affiliate_name ?? ""),
                    status: item.claimed ? t("invitations.status_claimed") : t("invitations.status_unclaimed"),
                    reminders: item.reminder_count ?? 0,
                    actions: "",
                  }))}
                  cellFactory={tableCellFactory}
                  defaultSortDirection="desc"
                />
                <SearchPagination
                  pagination={pagination}
                  pageLimit={pageLimit}
                  currentPage={currentPage}
                  onPageChange={update => setCurrentPage(update.currentPage)}
                  onPageLimitChange={newLimit => {
                    setPageLimit(newLimit);
                    setCurrentPage(0);
                  }}
                />
              </Container>
            )}
          </>
        )}
      </Box>

      <CDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <Box p={3}>
          <Text bold mb={2}>
            {t("invitations.confirm_delete")}
          </Text>
          {deleteTarget && (
            <Text mb={3} secondary>
              {deleteTarget.email ?? deleteTarget.phone_number ?? deleteTarget.name}
            </Text>
          )}
          <HFlex gap={2} justifyContent="flex-end">
            <Button onClick={() => setDeleteTarget(null)}>{t("buttons.cancel")}</Button>
            <Button variant="contained" color="secondary" disabled={isDeleting} onClick={handleDelete}>
              {t("buttons.delete")}
            </Button>
          </HFlex>
        </Box>
      </CDialog>
    </VFlex>
  );
};

export default Invitations;
